import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Palette, Recycle, HeartHandshake } from "lucide-react";
import api, { API } from "@/lib/api";
import { Marquee } from "@/components/Marquee";
import { ProductCard } from "@/components/ProductCard";
import { Seo } from "@/components/Seo";

const resolveImg = (u) => (u && u.startsWith("/api") ? `${API.replace(/\/api$/, "")}${u}` : u);

export default function Home() {
  const { data: products = [] } = useQuery({ queryKey: ["products"], queryFn: async () => (await api.get("/products?origin=official")).data });
  const { data: settings = {} } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const HERO = resolveImg(settings.hero_image) || "https://images.unsplash.com/photo-1513789181297-6f2ec112c0bc?crop=entropy&cs=srgb&fm=jpg&q=85";
  const GENZ = resolveImg(settings.community_image) || "https://images.unsplash.com/photo-1613915617430-8ab0fd7c6baf?crop=entropy&cs=srgb&fm=jpg&q=85";

  return (
    <div data-testid="home-page">
      <Seo title="The Hoodie Project® — Streetwear Hoodies, Reimagined"
        description="Heavyweight Classic Hoodies in black, grey and white. Buy, customize, and resell. A community-driven streetwear marketplace." />

      {/* HERO */}
      <section className="relative border-b border-border">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="flex flex-col justify-center px-5 py-16 md:px-12 md:py-24">
            <p className="mb-6 text-xs tracking-[0.3em] text-muted-foreground">EST. 2026 — FOR THE CULTURE</p>
            <h1 className="font-display text-5xl font-extrabold leading-[0.9] tracking-tighter md:text-7xl">
              ONE HOODIE.<br />INFINITE<br />LIVES.
            </h1>
            <p className="mt-8 max-w-md text-sm leading-relaxed text-muted-foreground">
              The Classic Hoodie is your blank canvas. Wear it. Paint it. Dye it. Resell it. Every drop puts money back in your pocket and your community.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/shop" data-testid="hero-shop-btn"
                className="group flex items-center gap-3 bg-white px-8 py-4 text-xs font-bold tracking-[0.2em] text-black transition-colors hover:bg-neutral-300">
                SHOP THE DROP <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/market" data-testid="hero-market-btn"
                className="flex items-center gap-3 border border-border px-8 py-4 text-xs font-bold tracking-[0.2em] transition-colors hover:bg-white hover:text-black">
                RESALE MARKET
              </Link>
            </div>
          </div>
          <div className="relative min-h-[50vh] border-t border-border lg:border-l lg:border-t-0">
            <img src={HERO} alt="Person in black hoodie" className="h-full max-h-[80vh] w-full object-cover md:max-h-none" />
          </div>
        </div>
      </section>

      <Marquee />

      {/* BENTO CONCEPT */}
      <section className="mx-auto max-w-[1600px] px-5 py-20 md:px-8">
        <div className="grid grid-cols-1 border border-border md:grid-cols-3">
          {[
            { icon: Palette, title: "BUY & CUSTOMIZE", body: "Grab a Classic Hoodie. Paint it, patch it, dye it — make it unmistakably yours." },
            { icon: Recycle, title: "RESELL FOR PROFIT", body: "List your one-of-one on our market. Set your price. Keep the bag, minus a small platform fee." },
            { icon: HeartHandshake, title: "RAISE FOR YOUR CAUSE", body: "Community partner drops turn customized hoodies into scholarships and real change for local causes." },
          ].map((c, i) => (
            <div key={i} className={`p-10 ${i < 2 ? "border-b border-border md:border-b-0 md:border-r" : ""}`}>
              <c.icon className="h-8 w-8" strokeWidth={1.5} />
              <h3 className="mt-6 font-display text-xl font-bold tracking-tight">{c.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{c.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="mx-auto max-w-[1600px] px-5 pb-20 md:px-8">
        <div className="mb-8 flex items-end justify-between border-b border-border pb-4">
          <h2 className="font-display text-3xl font-extrabold tracking-tighter md:text-4xl">THE CLASSIC HOODIE</h2>
          <Link to="/shop" className="text-xs tracking-[0.2em] text-muted-foreground hover:text-white">VIEW ALL →</Link>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      {/* COMMUNITY BANNER */}
      <section className="relative border-y border-border">
        <img src={GENZ} alt="Community" className="h-[50vh] w-full object-cover opacity-60" />
        <div className="absolute inset-0 flex flex-col items-start justify-center bg-gradient-to-r from-black/80 to-transparent px-5 md:px-12">
          <h2 className="font-display text-4xl font-extrabold leading-none tracking-tighter md:text-6xl">
            MORE THAN<br />MERCH.
          </h2>
          <p className="mt-4 max-w-md text-sm text-muted-foreground">We partner with community organizations to turn hoodies into opportunity. See what the community is building.</p>
          <Link to="/events" data-testid="community-events-btn" className="mt-8 bg-white px-8 py-4 text-xs font-bold tracking-[0.2em] text-black transition-colors hover:bg-neutral-300">
            SEE THE EVENTS
          </Link>
        </div>
      </section>
    </div>
  );
}
